import { Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import {
  CtButtonConfiguration,
  CtDatatableActionConfiguration,
  CtDatatableActionCustomConfiguration,
  CtDatatableConfiguration,
  CTGeneralService,
  CtModelConfiguration,
  MAT_RAISED_PRIMARY
} from "@ctsolution/ct-framework";
import { CTMGeneralService } from '../../_core/lib/general.service';
import { CtManagerService } from '../../_core/lib/ct-manager.service';
import { JwtService } from '../../_core/lib/jwt.service';
import { CtWebapiGenericResponse, CtWebapiService, DataRequest, MethodEnum } from "@ctsolution/ct-webapi";
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { UserRouteData } from '../../_core/route-data/user.route-data';
import { UserInfoFormTemplateRouteData } from '../../_core/route-data/user-info-form-template.route-data';

@Component({
  selector: 'app-profile',
  templateUrl: './profile.component.html',
  styleUrls: ['./profile.component.scss']
})
export class ProfileComponent implements OnInit {

  profileForm: FormGroup;
  isLoading = true;
  isSaving = false;
  userOid: number | null = null;
  originalProfileData: any = null;

  /** Tabella "I miei moduli" (dati da UserInfo_FormTemplate/OwnedOnlyList). */
  modulesDatatable: CtDatatableConfiguration | null = null;

  /** Template della cella "Visionato": pallino verde (visionato) / rosso (non visionato). */
  @ViewChild('viewedCell') viewedCellTpl: TemplateRef<any> | null = null;

  /** true se l'utente ha almeno un modulo: la sezione appare solo in questo caso. */
  hasModules = false;

  saveButton: CtButtonConfiguration = CtButtonConfiguration
    .create()
    .setLabel("Salva modifiche")
    .setAction(() => this.submit())
    .setMatherialOptions(MAT_RAISED_PRIMARY);

  constructor(
    private CTGeneralService: CTGeneralService,
    private generalService: CTMGeneralService,
    private jwtService: JwtService,
    private formBuilder: FormBuilder,
    private snackBar: MatSnackBar,
    private webapi: CtWebapiService,
    private managerService: CtManagerService,
    private router: Router
  ) {
    this.profileForm = this.formBuilder.group({
      Name: ['', Validators.required],
      Surname: ['', Validators.required]
    });
  }

  async ngOnInit() {
    await this.loadProfile();
  }

  async loadProfile() {

    this.isLoading = true;

    try {

      const userOid = await this.jwtService.getUserInfoOid();

      if (!userOid) {
        console.error('Impossibile recuperare l\'OID dell\'utente');
        this.isLoading = false;
        return;
      }

      this.userOid = +userOid;

      // Moduli dell'utente loggato: solo se la feature "Modules" è abilitata in SiteConfiguration.
      if (await this.managerService.hasEnabled.modules()) {
        this.loadModules();
      }

      const response: CtWebapiGenericResponse<any> = await this.generalService
        .getInfos({
          controller: 'User',
          oid: this.userOid
        })
        .toPromise();

      const profileData = response?.Result?.DataSource;

      if (profileData) {
        this.originalProfileData = profileData;

        this.profileForm.patchValue({
          Name: profileData.Name || '',
          Surname: profileData.Surname || ''
        });
      }

    } catch (error) {
      console.error('Errore durante il caricamento del profilo:', error);
    } finally {
      this.isLoading = false;
    }

  }

  /**
   * Carica i moduli dell'utente loggato tramite l'action dedicata del backend:
   * POST UserInfo_FormTemplate/OwnedOnlyList (come la lista normale, ma solo i propri).
   * La sezione "I miei moduli" appare solo se c'è almeno un modulo.
   */
  private loadModules(): void {

    const request = new DataRequest()
      .setController([UserInfoFormTemplateRouteData().controller!, 'OwnedOnlyList'])
      .setMethod(MethodEnum.POST)
      .setBody({ Pagination: { Page: 1, ElementsForPage: 1000 } })
      .setQueryParams({ hideSpinner: true });

    this.webapi
      .request(request)
      .subscribe({
        next: (response: CtWebapiGenericResponse<any>) => {

          const list = (response?.Result?.DataSource ?? []) as any[];

          const rows = list.map(row => {
            const readDate = row?.ReadCompletedDate ?? null;
            return {
              uftOid: row?.Oid,
              templateOid: row?.FormTemplate?.Oid ?? row?.Oid,
              name: row?.FormTemplate?.Name
                ?? row?.FormTemplate?.Title
                ?? row?.FormTemplate?.Description
                ?? row?.FormTemplate?.Code
                ?? `Modulo #${row?.FormTemplate?.Oid ?? row?.Oid}`,
              viewed: !!readDate,
              readDate: readDate ? new Date(readDate).toLocaleDateString('it-IT') : '-'
            };
          });

          this.hasModules = rows.length > 0;

          this.modulesDatatable = CtDatatableConfiguration
            .create()
            .setScrollbarH(true)
            .setFiltersEnabled(false)
            // Dati statici già in memoria: paginazione/sorting locali e nessun indicatore di caricamento.
            .setExternalPaging(false)
            .setExternalSorting(false)
            .setLoadingIndicator(false)
            .setColumns([
              { prop: 'name', name: 'Titolo', sortable: false },
              { prop: 'viewed', name: 'Visionato', width: 120, sortable: false, cellTemplate: this.viewedCellTpl },
              { prop: 'readDate', name: 'Data visione', width: 180, sortable: false }
            ] as any)
            .setRows(rows)
            // Azione di riga: apre il dettaglio del modulo.
            .setCustomDatatableActionConfiguration(
              CtDatatableActionCustomConfiguration
                .create()
                .setActionButtonConfig(
                  CtDatatableActionConfiguration
                    .create()
                    .setIcon('chevron_right')
                    .setColor('primary'))
                .setCallback((row: any) => this.openModule(row)));

        },
        error: (err: any) => {
          console.error('Errore durante il caricamento dei moduli:', err);
          this.modulesDatatable = null;
          this.hasModules = false;
        }
      });

  }

  /**
   * Apre il dettaglio del modulo, passando l'Oid del template e quello dell'associazione
   * (uftOid) che serve al dettaglio per valorizzare ReadCompletedDate al salvataggio.
   */
  openModule(module: { uftOid: number; templateOid: number }): void {

    if (!module?.templateOid) return;

    this.router.navigate(['/', 'profile', 'form-template', module.templateOid], {
      queryParams: { uftOid: module.uftOid }
    });

  }

  /**
   * I dati sono statici (già caricati da OwnedOnlyList): all'evento reload del datatable
   * spegniamo l'indicatore di caricamento (che il ct-datatable accende in fase di setup).
   */
  onModulesReload(): void {
    this.modulesDatatable?.setLoadingIndicator(false);
  }

  async submit() {

    this.profileForm.markAllAsTouched();

    if (this.profileForm.valid) {

      this.isSaving = true;

      const formValues = this.profileForm.getRawValue();

      const dataToSend = {
        ...this.originalProfileData,
        Name: formValues.Name,
        Surname: formValues.Surname
      };

      const configuration = CtModelConfiguration
        .create()
        .setRouteData(
          UserRouteData().setId(this.userOid!)
        );

      try {

        await this.generalService.putData(configuration, dataToSend);

        this.showSuccessMessage('Profilo aggiornato con successo');

        await this.loadProfile();

      } catch (error) {
        console.error('Errore durante il salvataggio:', error);
        this.showErrorMessage('Errore durante il salvataggio del profilo');
      } finally {
        this.isSaving = false;
      }

    }

  }

  hasError(fieldName: string, errorType: string): boolean {
    const field = this.profileForm.get(fieldName);
    return !!(field?.hasError(errorType) && (field?.dirty || field?.touched));
  }

  private showSuccessMessage(message: string): void {
    this.snackBar.open(message, 'Chiudi', {
      duration: 5000,
      horizontalPosition: 'center',
      verticalPosition: 'bottom',
      panelClass: ['success-snackbar']
    });
  }

  private showErrorMessage(message: string): void {
    this.snackBar.open(message, 'Chiudi', {
      duration: 7000,
      horizontalPosition: 'center',
      verticalPosition: 'bottom',
      panelClass: ['error-snackbar']
    });
  }

}
